/**
 * /admin/users - the merged roster (formerly /admin/access + /admin/subscriptions).
 *
 * Thin route: it owns the URL search contract (validateSearch) and redirects
 * the retired drawer link. The roster body lives in
 * components/admin/users/roster/UsersRoster.tsx and is the page's only content.
 *
 * ── The retired Access-requests tab ─────────────────────────────────────────
 * The queue used to be a second Radix tab here, which put two tab rows on the
 * screen at once (the switcher, then the roster's own lifecycle tabs) and gave
 * the queue no count, no address and no survival across a reload. It is a
 * sibling route now: /admin/access-requests.
 *
 * ── The retired `?user=` param ──────────────────────────────────────────────
 * The drawer is gone; a member opens as a real page at /admin/users/$userId.
 * Old links, bookmarks and any cached tab still carry `?user=<key>`, so the
 * route redirects them to the record page and hands the REST of the roster
 * search across as `?from=`, which is what the record page's "Back to Users"
 * reads. The roster's key spelling (`pending:<id>`) becomes the record route's
 * URL-safe `pending_<id>` through `rosterKeyToRecordKey`.
 */
import { createFileRoute, redirect } from '@tanstack/react-router';
import { UsersRoster } from '@/components/admin/users/roster/UsersRoster';
import { rosterKeyToRecordKey } from '@/api/admin-users';
import {
  parseUsersSearch,
  usersSearchToQueryString,
  type UsersSearch,
} from '@/lib/admin/user-filters';

export const Route = createFileRoute('/admin/users')({
  component: UsersPage,
  validateSearch: (search: Record<string, unknown>): UsersSearch => parseUsersSearch(search),
  beforeLoad: ({ search }) => {
    const key = (search as UsersSearch).user;
    if (!key) return;
    const from = usersSearchToQueryString({ ...(search as UsersSearch), user: undefined });
    throw redirect({
      to: '/admin/users/$userId',
      params: { userId: rosterKeyToRecordKey(key) },
      search: from ? { from } : {},
      replace: true,
    });
  },
});

function UsersPage() {
  return <UsersRoster />;
}
